import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Settings, MessageCircle, Clock, Save, Calendar, Plus, X, AlertTriangle, QrCode, Download, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getConfiguracion, updateConfiguracion, cambiarCredenciales } from "@/lib/api";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { QRCodeSVG } from "qrcode.react";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function CambiarCredencialesCard() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error("Introduce la contraseña actual"); return; }
    if (!newUsername && !newPassword) { toast.error("Introduce nuevo usuario o contraseña"); return; }
    setSaving(true);
    try {
      const res = await cambiarCredenciales({ current_password: currentPassword, new_username: newUsername || undefined, new_password: newPassword || undefined });
      localStorage.setItem("admin_token", res.data.token);
      toast.success("Credenciales actualizadas");
      setCurrentPassword(""); setNewUsername(""); setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Cambiar acceso</CardTitle>
        <CardDescription>Cambia tu usuario y/o contraseña del panel de administracion</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Contraseña actual *</Label>
            <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="h-11" data-testid="input-current-password" placeholder="Tu contraseña actual" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nuevo usuario (opcional)</Label>
              <Input value={newUsername} onChange={e => setNewUsername(e.target.value)} className="h-11" data-testid="input-new-username" placeholder="Dejar vacio para no cambiar" />
            </div>
            <div>
              <Label>Nueva contraseña (opcional)</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-11" data-testid="input-new-password" placeholder="Min. 6 caracteres" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="gap-2" data-testid="btn-cambiar-credenciales">
            <Lock className="h-4 w-4" /> {saving ? "Guardando..." : "Cambiar credenciales"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function createDefaultHorarios() {
  return [
    { dia: 0, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 1, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 2, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 3, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 4, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 5, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 6, inicio: "09:00", fin: "20:00", activo: false }
  ];
}

export default function AdminConfiguracion() {
  const [nombreNegocio, setNombreNegocio] = useState("Barbería Clásica");
  const [whatsapp, setWhatsapp] = useState("");
  const [mensajeWhatsapp, setMensajeWhatsapp] = useState("Hola, me gustaría información sobre la peluquería");
  const [horarioGeneral, setHorarioGeneral] = useState(createDefaultHorarios());
  const [intervaloCitas, setIntervaloCitas] = useState(15);
  const [festivos, setFestivos] = useState([]);
  const [direccion, setDireccion] = useState("");
  const [diasMinimosCancelacion, setDiasMinimosCancelacion] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal para añadir festivo
  const [showFestivoModal, setShowFestivoModal] = useState(false);
  const [nuevoFestivo, setNuevoFestivo] = useState({ fecha: "", nombre: "" });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await getConfiguracion();
      if (res.data) {
        setNombreNegocio(res.data.nombre_negocio || "Barbería Clásica");
        setWhatsapp(res.data.whatsapp || "");
        setMensajeWhatsapp(res.data.mensaje_whatsapp || "Hola, me gustaría información sobre la peluquería");
        setHorarioGeneral(res.data.horario_general && res.data.horario_general.length > 0 
          ? res.data.horario_general 
          : createDefaultHorarios());
        setIntervaloCitas(res.data.intervalo_citas || 15);
        setFestivos(res.data.festivos || []);
        setDireccion(res.data.direccion || "");
        setDiasMinimosCancelacion(res.data.dias_minimos_cancelacion ?? 2);
      }
    } catch (error) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateConfiguracion({
        id: "config_principal",
        nombre_negocio: nombreNegocio,
        whatsapp: whatsapp,
        mensaje_whatsapp: mensajeWhatsapp,
        horario_general: horarioGeneral,
        intervalo_citas: intervaloCitas,
        festivos: festivos,
        direccion: direccion,
        dias_minimos_cancelacion: diasMinimosCancelacion
      });
      toast.success("Configuración guardada correctamente");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const updateHorario = (diaIndex, field, value) => {
    const newHorarios = horarioGeneral.map((h, i) =>
      i === diaIndex ? { ...h, [field]: value } : h
    );
    setHorarioGeneral(newHorarios);
  };

  const updateHorarioMulti = (diaIndex, updates) => {
    const newHorarios = horarioGeneral.map((h, i) =>
      i === diaIndex ? { ...h, ...updates } : h
    );
    setHorarioGeneral(newHorarios);
  };

  const downloadQR = (svgId, filename) => {
    const svg = document.getElementById(svgId);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 10, 10, 380, 380);
      const a = document.createElement("a");
      a.download = `${filename}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleAddFestivo = () => {
    if (!nuevoFestivo.fecha || !nuevoFestivo.nombre) {
      toast.error("Completa la fecha y el nombre del festivo");
      return;
    }
    setFestivos([...festivos, nuevoFestivo]);
    setNuevoFestivo({ fecha: "", nombre: "" });
    setShowFestivoModal(false);
    toast.success("Festivo añadido");
  };

  const handleRemoveFestivo = (index) => {
    const newFestivos = festivos.filter((_, i) => i !== index);
    setFestivos(newFestivos);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div data-testid="admin-configuracion">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading">Configuración</h1>
          <p className="text-muted-foreground mt-1">Personaliza todos los aspectos de tu negocio</p>
        </div>
        <Button
          className="h-12 gap-2"
          onClick={handleSave}
          disabled={saving}
          data-testid="btn-guardar-config"
        >
          {saving ? (
            <>
              <div className="spinner w-4 h-4 border-white" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Guardar todo
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-6">
        {/* Datos del negocio */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Datos del negocio
            </CardTitle>
            <CardDescription>
              Información básica que verán tus clientes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nombre del negocio</Label>
                <Input
                  placeholder="Barbería Clásica"
                  value={nombreNegocio}
                  onChange={e => setNombreNegocio(e.target.value)}
                  className="h-12"
                  data-testid="input-nombre-negocio"
                />
              </div>
              <div>
                <Label>Intervalo entre citas (minutos)</Label>
                <Input
                  type="number"
                  min="5"
                  step="5"
                  value={intervaloCitas}
                  onChange={e => setIntervaloCitas(parseInt(e.target.value) || 15)}
                  className="h-12"
                  data-testid="input-intervalo"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Cada cuántos minutos se generan los huecos disponibles
                </p>
              </div>
            </div>
            <div>
              <Label>Dirección (opcional)</Label>
              <Input
                placeholder="Calle Mayor 15, Madrid"
                value={direccion}
                onChange={e => setDireccion(e.target.value)}
                className="h-12"
                data-testid="input-direccion"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Se mostrará en la confirmación de la cita
              </p>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              WhatsApp del negocio
            </CardTitle>
            <CardDescription>
              Configura el botón de contacto por WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Número de WhatsApp</Label>
              <Input
                placeholder="34612345678"
                value={whatsapp}
                onChange={e => setWhatsapp(e.target.value)}
                className="h-12"
                data-testid="input-whatsapp"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Incluye código de país (34 para España). El botón aparecerá cuando pongas un número.
              </p>
            </div>
            <div>
              <Label>Mensaje predeterminado</Label>
              <Textarea
                placeholder="Hola, me gustaría información sobre la peluquería"
                value={mensajeWhatsapp}
                onChange={e => setMensajeWhatsapp(e.target.value)}
                className="min-h-[80px]"
                data-testid="input-mensaje-whatsapp"
              />
            </div>
          </CardContent>
        </Card>

        {/* Horario general */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Horario del negocio
            </CardTitle>
            <CardDescription>
              Horario base. Cada peluquero puede tener su propio horario en "Peluqueros"
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {horarioGeneral.map((horario, idx) => (
                <div key={idx} className="py-3 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={horario.activo}
                      onCheckedChange={v => updateHorario(idx, "activo", v)}
                      data-testid={`switch-dia-${idx}`}
                    />
                    <span className="w-28 font-medium">{DIAS[idx]}</span>
                    {horario.activo ? (
                      <div className="flex flex-col gap-2 flex-1">
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={horario.inicio}
                            onChange={e => updateHorario(idx, "inicio", e.target.value)}
                            className="w-32 h-10"
                          />
                          <span className="text-muted-foreground">a</span>
                          <Input
                            type="time"
                            value={horario.fin}
                            onChange={e => updateHorario(idx, "fin", e.target.value)}
                            className="w-32 h-10"
                          />
                          {!horario.inicio2 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-muted-foreground"
                              onClick={() => updateHorarioMulti(idx, { inicio2: "16:00", fin2: "20:00" })}
                              data-testid={`btn-add-turno-${idx}`}
                            >
                              + Turno tarde
                            </Button>
                          )}
                        </div>
                        {horario.inicio2 && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={horario.inicio2}
                              onChange={e => updateHorario(idx, "inicio2", e.target.value)}
                              className="w-32 h-10"
                            />
                            <span className="text-muted-foreground">a</span>
                            <Input
                              type="time"
                              value={horario.fin2}
                              onChange={e => updateHorario(idx, "fin2", e.target.value)}
                              className="w-32 h-10"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive h-8 w-8"
                              onClick={() => updateHorarioMulti(idx, { inicio2: null, fin2: null })}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Badge variant="secondary">Cerrado</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Festivos */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Días festivos / Cerrados
                </CardTitle>
                <CardDescription>
                  Días en los que el negocio estará cerrado (no se podrán reservar citas)
                </CardDescription>
              </div>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => setShowFestivoModal(true)}
                data-testid="btn-add-festivo"
              >
                <Plus className="h-4 w-4" />
                Añadir festivo
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {festivos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No hay festivos configurados</p>
                <p className="text-sm">Añade los días en que no trabajas (vacaciones, festivos locales...)</p>
              </div>
            ) : (
              <div className="space-y-2">
                {festivos.map((festivo, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                  >
                    <div>
                      <span className="font-medium">{festivo.nombre}</span>
                      <span className="text-muted-foreground ml-2">
                        {format(new Date(festivo.fecha), "d 'de' MMMM yyyy", { locale: es })}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleRemoveFestivo(idx)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cancelación */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Politica de cancelacion
            </CardTitle>
            <CardDescription>
              Tiempo minimo para que los clientes puedan cancelar una cita
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Label>Minimo</Label>
              <Input
                type="number"
                min={0}
                max={14}
                value={diasMinimosCancelacion}
                onChange={e => setDiasMinimosCancelacion(parseInt(e.target.value) || 0)}
                className="w-20 h-10"
                data-testid="input-dias-cancelacion"
              />
              <span className="text-muted-foreground">dias de antelacion</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Si un cliente intenta cancelar con menos de {diasMinimosCancelacion} dias, le pedira que contacte por telefono.
            </p>
          </CardContent>
        </Card>

        {/* QR Codes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Codigos QR
            </CardTitle>
            <CardDescription>
              Imprime estos QR y ponlos en la peluqueria para que los clientes puedan reservar o cancelar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* QR Reservar */}
              <div className="text-center space-y-3">
                <h3 className="font-heading text-lg">Reservar cita</h3>
                <div className="inline-block p-4 bg-white rounded-xl border-2">
                  <QRCodeSVG
                    id="qr-reservar"
                    value={`${window.location.origin}/reservar`}
                    size={180}
                    level="H"
                  />
                </div>
                <p className="text-xs text-muted-foreground">{window.location.origin}/reservar</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => downloadQR("qr-reservar", "qr-reservar-cita")}
                  data-testid="btn-download-qr-reservar"
                >
                  <Download className="h-4 w-4" />
                  Descargar QR
                </Button>
              </div>
              
              {/* QR Cancelar */}
              <div className="text-center space-y-3">
                <h3 className="font-heading text-lg">Cancelar cita</h3>
                <div className="inline-block p-4 bg-white rounded-xl border-2">
                  <QRCodeSVG
                    id="qr-cancelar"
                    value={`${window.location.origin}/cancelar`}
                    size={180}
                    level="H"
                  />
                </div>
                <p className="text-xs text-muted-foreground">{window.location.origin}/cancelar</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => downloadQR("qr-cancelar", "qr-cancelar-cita")}
                  data-testid="btn-download-qr-cancelar"
                >
                  <Download className="h-4 w-4" />
                  Descargar QR
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cambiar contraseña */}
        <CambiarCredencialesCard />

        {/* Información importante */}
        <Card className="border-accent/50 bg-accent/5">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg mb-2">💡 Consejos</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Servicios:</strong> Puedes modificar precios y duraciones en la sección "Servicios"</li>
              <li>• <strong>Peluqueros:</strong> Cada peluquero puede tener su propio horario y días libres</li>
              <li>• <strong>Bloqueos:</strong> Usa "Bloquear hora" en el Calendario para descansos o comidas</li>
              <li>• <strong>Citas manuales:</strong> Desde el Calendario puedes añadir citas de clientes que llamen o escriban</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Modal añadir festivo */}
      <Dialog open={showFestivoModal} onOpenChange={setShowFestivoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Añadir día festivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={nuevoFestivo.fecha}
                onChange={e => setNuevoFestivo({ ...nuevoFestivo, fecha: e.target.value })}
                className="h-12"
                data-testid="input-festivo-fecha"
              />
            </div>
            <div>
              <Label>Nombre / Motivo</Label>
              <Input
                placeholder="Ej: Navidad, Vacaciones, Fiesta local..."
                value={nuevoFestivo.nombre}
                onChange={e => setNuevoFestivo({ ...nuevoFestivo, nombre: e.target.value })}
                className="h-12"
                data-testid="input-festivo-nombre"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFestivoModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddFestivo} data-testid="btn-guardar-festivo">
              Añadir festivo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
